import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Container, Form, InputGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { formatMomentDate } from "../../../utils/dateHelper";
import CommonButton from "../../../components/commonButton";
import CustomSelect from "../../../components/form/CustomSelect";
import InputField from "../../../components/form/InputField";
import { ArrowLeftIcon } from "../../../assets/icons";

import { routes } from "../../../routes/pageRoutes";
import { useLoader } from "../../../context/LoaderContext";
import { useToast } from "../../../context/TostContext";
import { bulkOfferSchema } from "../../../schema/validationSchemas";

import {
    createBulkOffer,
    updateBulkOffer,
    fetchListingDetails,
    fetchMarketplaceConfig,
    clearListingDetails,
    uploadMarketplaceMedia,
    deleteMarketplaceMedia,
    fetchMarketplaceCategoriesPaginated
} from "../../../redux/slice/marketplaceSlice";
import { fetchActiveStates, fetchActiveCities, fetchCountries } from "../../../redux/slice/locationSlice";
import { fetchAllCompanies } from "../../../redux/slice/companySlice";
import { Minus, Plus } from "lucide-react";
import { CheckboxOption, FormFeedback, FormLabel } from "../../../components/form/utils";

// Collapsible section wrapper matching Reference design
const FormSection = ({ title, children, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="bg-white rounded-2 common-shadow mb-3 overflow-hidden">
            <div
                className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom cursor-pointer"
                onClick={() => setOpen(o => !o)}
            >
                <h6 className="mb-0 fw-semibold primary">{title}</h6>
                <i className="fs-sm primary lh-1">
                    {open ? <Minus /> : <Plus />}
                </i>
            </div>
            {open && <div className="p-4 grid gap-3">{children}</div>}
        </div>
    );
};

const serviceabilityOptions = [
    { label: "Whole Country (Pan India)", value: "pan_india" },
    { label: "Specific States", value: "states" },
    { label: "Specific Cities", value: "cities" }
];
const customOptionStyle = {

    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? "transparent"
            : state.isFocused
                ? "var(--bg-main)"
                : state.isSelected && state.isFocused ? "var(--bg-main)" : "transparent",
        color: 'var(-text-primary)',
        ':active': {
            backgroundColor: "var(--primary-100)"
        },
        ':hover': {
            backgroundColor: "var(--bg-main)"
        }
    })
};
const BulkOfferForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { id } = useParams();
    const isEdit = !!id;

    const fromPath = location.state?.from;
    const companyIdFromState = location.state?.companyId;
    const companyNameFromState = location.state?.companyName;

    const headerPortalTarget = document.getElementById("header-portal-target");

    // console.log("isEdit --=-----=-==-===--", isEdit);
    const { setLoading } = useLoader();
    const toast = useToast();
    const { details, config } = useSelector((state) => state.marketplace);
    const { allCompanies } = useSelector((state) => state.company);

    useEffect(() => {
        dispatch(fetchAllCompanies({ page: 1, limit: 10, search: "", status: "active" }));
        dispatch(fetchMarketplaceConfig());
        if (isEdit) {
            dispatch(fetchListingDetails({ id, seller_type: 'company' }));
        } else {
            dispatch(clearListingDetails());
        }
    }, [dispatch, id, isEdit]);

    const companyOptions = React.useMemo(() => {
        let options = (allCompanies || []).map(c => ({ value: c.id, label: c.name }));
        if (companyIdFromState && !options.some(opt => opt.value === companyIdFromState)) {
            options.push({
                value: companyIdFromState,
                label: companyNameFromState || "Selected Company"
            });
        }
        return options;
    }, [allCompanies, companyIdFromState, companyNameFromState]);

    const initialValues = {
        company_id: companyIdFromState || details?.seller_id || "",
        category_id: details?.category_id || "",
        name: details?.name || "",
        brief_description: details?.brief_description || "",
        sku: details?.company_product_details?.sku || "",
        country_id: details?.company_product_details?.country_id || "",
        variations: details?.company_product_details?.variations || "",
        warranty: details?.company_product_details?.warranty || "",

        product_description: details?.company_product_details?.product_description || "",
        features: details?.company_product_details?.features || "",
        specifications: details?.company_product_details?.specifications || "",
        terms: details?.company_product_details?.terms || "",

        mrp: details?.company_offer_details?.mrp || "",
        offer_price: details?.company_offer_details?.offer_price || "",
        lot_size: details?.company_offer_details?.lot_size || "",
        available_qty: details?.company_offer_details?.available_qty || "",
        start_date: details?.company_offer_details?.start_date
            ? formatMomentDate(details.company_offer_details.start_date, "YYYY-MM-DD")
            : "",
        end_date: details?.company_offer_details?.end_date
            ? formatMomentDate(details.company_offer_details.end_date, "YYYY-MM-DD")
            : "",
        serviceability: details?.company_offer_details?.serviceability || "",
        serviceability_type: details?.company_offer_details?.serviceability_type || "pan_india",
        serviceable_ids: (() => {
            const type = details?.company_offer_details?.serviceability_type;
            if (type === 'states') {
                const stateIds = new Set();
                details.serviceable_cities?.forEach(sc => {
                    if (sc.city?.state_id) stateIds.add(sc.city.state_id);
                });
                return Array.from(stateIds);
            } else if (type === 'cities') {
                return (details.serviceable_cities || []).map(c => c.city_id);
            }
            return [];
        })(),

        delivery_charges: details?.company_offer_details?.delivery_charges || "",
        delivery_taken_before: details?.company_offer_details?.delivery_taken_before || "",
        delivery_start_date: details?.company_offer_details?.delivery_start_date
            ? formatMomentDate(details.company_offer_details.delivery_start_date, "YYYY-MM-DD")
            : "",
        billing_from: details?.company_offer_details?.billing_from || "",
        booking_deposit: details?.company_offer_details?.booking_deposit || "",
        payment_credit_policy: details?.company_offer_details?.payment_credit_policy || "",

        company_type: details?.company_seller_info?.company_type || "",
        plant_address: details?.company_seller_info?.plant_address || "",
        marketed_by: details?.company_seller_info?.marketed_by || "",
        manufactured_by: details?.company_seller_info?.manufactured_by || "",

        state_id: details?.state_id || "",
        city_id: details?.city_id || "",

        images: [],
        video: null,
        brochure: null,
        existingImages: details?.photos || [],
        existingAPlusImages: details?.a_plus_photos || [],
        existingVideo: details?.video || null,
        existingBrochure: details?.document || null,
    };

    const fetchCountriesOptions = useCallback(async (search, page) => {
        try {
            const result = await dispatch(fetchCountries({ search, page })).unwrap();
            return result;
        } catch (error) {
            console.error("Error fetching countries:", error);
            return { data: [], meta: { page: 1, pages: 1 } };
        }
    }, [dispatch]);

    const fetchCategoriesOptions = useCallback(async (search, page) => {
        try {
            const result = await dispatch(fetchMarketplaceCategoriesPaginated({ search, page })).unwrap();
            return result;
        } catch (error) {
            console.error("Error fetching categories:", error);
            return { data: [], meta: { page: 1, pages: 1 } };
        }
    }, [dispatch]);

    const fetchStatesOptions = useCallback(async (search, page) => {
        try {
            const result = await dispatch(fetchActiveStates({ search, page })).unwrap();
            return result;
        } catch (error) {
            console.error("Error fetching states:", error);
            return { data: [], meta: { page: 1, pages: 1 } };
        }
    }, [dispatch]);

    const fetchCitiesOptionsForState = useCallback((stateId) => async (search, page) => {
        try {
            const result = await dispatch(fetchActiveCities({ search, page, state_ids: stateId })).unwrap();
            return result;
        } catch (error) {
            console.error("Error fetching cities:", error);
            return { data: [], meta: { page: 1, pages: 1 } };
        }
    }, [dispatch]);

    const categoryDataMapper = useCallback((data) => {
        return (data || []).map(parent => ({
            label: parent.name,
            options: (parent.subcategories || []).map(sub => ({
                value: sub.id,
                label: sub.name
            }))
        }));
    }, []);

    const handleSubmit = async (values, { setSubmitting }) => {
        setLoading(true);
        try {
            const {
                serviceability_type, serviceable_ids,
                images, video, brochure, existingImages, existingVideo, existingBrochure,
                ...rest
            } = values;

            const payload = {
                ...rest,
                photos: (values.existingImages || []).map(img => ({ id: img.id })),
                a_plus_photos: (values.existingAPlusImages || []).map(img => ({ id: img.id })),
                video: values.existingVideo ? { id: values.existingVideo.id } : null,
                document: values.existingBrochure ? { id: values.existingBrochure.id } : null,
                serviceability_type,
                state_ids: serviceability_type === 'states' ? serviceable_ids : [],
                city_ids: serviceability_type === 'cities' ? serviceable_ids : []
            };

            if (isEdit) {
                await dispatch(updateBulkOffer({ id, data: payload })).unwrap();
            } else {
                await dispatch(createBulkOffer(payload)).unwrap();
            }
            toast.success(isEdit ? "Offer updated successfully" : "Offer created successfully", "success");
            navigate(fromPath || routes.marketplaceBulkOffers);
        } catch (error) {
            console.error("Error in handleSubmit :::", error);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const handleRemoveMedia = async (mediaId, type, setFieldValue, currentValues) => {
        if (!mediaId) return;
        setLoading(true);
        try {
            await dispatch(deleteMarketplaceMedia(mediaId)).unwrap();
            if (type === 'photos') {
                const updated = currentValues.existingImages.filter(img => img.id !== mediaId);
                setFieldValue("existingImages", updated);
            } else if (type === 'a_plus_photos') {
                const updated = currentValues.existingAPlusImages.filter(img => img.id !== mediaId);
                setFieldValue("existingAPlusImages", updated);
            } else if (type === 'video') {
                setFieldValue("existingVideo", null);
            } else if (type === 'document') {
                setFieldValue("existingBrochure", null);
            }
        } catch (error) {
            toast.error("Failed to delete media");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid className={`px-0 ${headerPortalTarget ? "mt-4" : ""}`}>
            <Formik
                initialValues={initialValues}
                validationSchema={bulkOfferSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting, handleChange, handleBlur }) => {

                    const renderHeader = (isPortal = false) => (
                        <div className="d-flex align-items-center justify-content-between w-100">
                            <div className="d-flex align-items-center gap-2">
                                <Link
                                    to={fromPath || routes.marketplaceBulkOffers}
                                    className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
                                >
                                    <ArrowLeftIcon size={22} strokeWidth="2.2" />
                                </Link>
                                <h4 className="fw-semibold mb-0" style={{ color: '#444050', fontSize: '18px' }}>
                                    {isEdit ? "Edit Bulk Offer" : "Create Bulk Offer"}
                                </h4>
                            </div>

                            <div className="d-flex gap-2">
                                <CommonButton
                                    type="button"
                                    title="Cancel"
                                    variant="outline"
                                    onClick={() => navigate(fromPath || routes.marketplaceBulkOffers)}
                                />
                                <CommonButton
                                    type={isPortal ? "button" : "submit"}
                                    title={isEdit ? "Update" : "Submit"}
                                    variant="primary"
                                    disabled={isSubmitting}
                                    onClick={isPortal ? handleSubmit : undefined}
                                />
                            </div>
                        </div>
                    );

                    return (
                        <Form onSubmit={handleSubmit}>
                            {/* Header row — back link + action buttons */}
                            {headerPortalTarget ? (
                                createPortal(
                                    renderHeader(true),
                                    headerPortalTarget
                                )
                            ) : (
                                <div className="d-flex align-items-center justify-content-between mb-4 sticky-top bg-white z-1" style={{ top: "80px", padding: "10px 0" }}>
                                    {renderHeader(false)}
                                </div>
                            )}

                            {/* ── BASIC DETAILS ── */}
                            <FormSection title="Basic Details">

                                {/* Company (create only) */}
                                {!isEdit && (
                                    <CustomSelect
                                        label={{ name: "Company Name" }}
                                        name="company_id"
                                        required={true}
                                        value={values.company_id}
                                        onChange={(opt) => setFieldValue("company_id", opt ? opt.value : "")}
                                        options={companyOptions}
                                        placeholder="Select Company"
                                        validation={{
                                            isInvalid: touched.company_id && errors.company_id,
                                            message: errors.company_id,
                                        }}
                                        isDisabled={!!companyIdFromState}
                                        className="g-col-12"
                                    />
                                )}
                                <InputField
                                    className="g-col-6"
                                    label={{ name: "Offer Name" }}
                                    required={true}
                                    input={{
                                        name: "name",
                                        value: values.name,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "Ordinary Portland Cement",
                                    }}
                                    validation={{
                                        isInvalid: touched.name && errors.name,
                                        message: errors.name,
                                    }}
                                />
                                <InputField
                                    className="g-col-6"
                                    label={{ name: "Product SKU" }}
                                    required={true}
                                    input={{
                                        name: "sku",
                                        value: values.sku,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "SKU-12345",
                                    }}
                                    validation={{
                                        isInvalid: touched.sku && errors.sku,
                                        message: errors.sku,
                                    }}
                                />
                                <InputField
                                    className="g-col-12"
                                    label={{ name: "Offer Brief Description" }}
                                    required={true}
                                    input={{
                                        name: "brief_description",
                                        as: "textarea",
                                        rows: 3,
                                        value: values.brief_description,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "high-strength concrete, mortars, general-purpose ready-mixes.",
                                    }}
                                    validation={{
                                        isInvalid: touched.brief_description && errors.brief_description,
                                        message: errors.brief_description,
                                    }}
                                />
                                <CustomSelect
                                    className="g-col-4"
                                    label={{ name: "Country Of Origin" }}
                                    name="country_id"
                                    required={true}
                                    value={values.country_id}
                                    onChange={(name, val) => setFieldValue(name, val)}
                                    fetchOptions={fetchCountriesOptions}
                                    placeholder="Select Country"
                                    isClearable={true}
                                    validation={{
                                        isInvalid: touched.country_id && errors.country_id,
                                        message: errors.country_id,
                                    }}
                                    menuPosition="fixed"
                                />
                                <InputField
                                    className="g-col-4"
                                    label={{ name: "Variations" }}
                                    required={true}
                                    input={{
                                        name: "variations",
                                        value: values.variations,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "50 KG",
                                    }}
                                    validation={{
                                        isInvalid: touched.variations && errors.variations,
                                        message: errors.variations,
                                    }}
                                />

                                <CustomSelect
                                    className="g-col-4"
                                    label={{ name: "Category" }}
                                    name="category_id"
                                    required={true}
                                    value={values.category_id}
                                    onChange={(name, val) => setFieldValue(name, val)}
                                    fetchOptions={fetchCategoriesOptions}
                                    dataMapper={categoryDataMapper}
                                    placeholder="Select Category"
                                    validation={{
                                        isInvalid: touched.category_id && errors.category_id,
                                        message: errors.category_id,
                                    }}
                                    menuPosition="fixed"
                                />


                            </FormSection>

                            {/* ── PRODUCT DETAILS ── */}
                            <FormSection title="Product Details">

                                <InputField
                                    className="g-col-12"
                                    label={{ name: "Product Description" }}
                                    required={true}
                                    input={{
                                        name: "product_description",
                                        as: "textarea",
                                        rows: 4,
                                        value: values.product_description,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "A strong and durable multipurpose storage rack designed to keep your space organized and clutter-free.",
                                    }}
                                    validation={{
                                        isInvalid: touched.product_description && errors.product_description,
                                        message: errors.product_description,
                                    }}
                                />
                                <InputField
                                    className="g-col-12"
                                    label={{ name: "Key Features" }}
                                    required={true}
                                    input={{
                                        name: "features",
                                        as: "textarea",
                                        rows: 4,
                                        value: values.features,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "• A strong and durable multipurpose storage rack\n• Ideal for home, office, or warehouse storage",
                                    }}
                                    validation={{
                                        isInvalid: touched.features && errors.features,
                                        message: errors.features,
                                    }}
                                />
                                <InputField
                                    className="g-col-6"
                                    label={{ name: "Product Specifications" }}
                                    input={{
                                        name: "specifications",
                                        as: "textarea",
                                        rows: 3,
                                        value: values.specifications,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "Technical details...",
                                    }}
                                />
                                <InputField
                                    className="g-col-6"
                                    label={{ name: "Product Warranty" }}
                                    input={{
                                        name: "warranty",
                                        value: values.warranty,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "1 year",
                                    }}
                                />
                                <InputField
                                    className="g-col-12"
                                    label={{ name: "Terms & Conditions" }}
                                    input={{
                                        name: "terms",
                                        as: "textarea",
                                        rows: 3,
                                        value: values.terms,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "High strength 100 tons, traction, general purpose, study mins.",
                                    }}
                                />

                            </FormSection>

                            {/* ── PRICING INFORMATION ── */}
                            <FormSection title="Pricing Information">

                                <Form.Group controlId="mrp" className="g-col-3">
                                    <FormLabel labelName="MRP" required={true} id="mrp" />
                                    <InputGroup>
                                        <InputGroup.Text id="mrp-addon">₹</InputGroup.Text>
                                        <Form.Control
                                            id="mrp"
                                            type="number"
                                            name="mrp"
                                            value={values.mrp}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="320"
                                            aria-label="MRP"
                                            aria-describedby="mrp-addon"
                                        />
                                    </InputGroup>
                                    {errors.mrp && touched.mrp && <FormFeedback message={errors.mrp} />}
                                </Form.Group>
                                <Form.Group controlId="offer_price" className="g-col-3">
                                    <FormLabel labelName="Offer/Bulk Price" required={true} id="offer_price" />
                                    <InputGroup>
                                        <InputGroup.Text id="offer_price-addon">₹</InputGroup.Text>
                                        <Form.Control
                                            id="offer_price"
                                            type="number"
                                            name="offer_price"
                                            value={values.offer_price}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="1500"
                                            aria-label="Offer/Bulk Price"
                                            aria-describedby="offer_price-addon"
                                        />
                                    </InputGroup>
                                    {errors.offer_price && touched.offer_price && <FormFeedback message={errors.offer_price} />}
                                </Form.Group>

                                <InputField className="g-col-2" label={{ name: "Discount %" }} input={{
                                    value:
                                        values.mrp && values.offer_price
                                            ? (((values.mrp - values.offer_price) / values.mrp) * 100).toFixed(2) + "%"
                                            : "0%",
                                    disabled: true,
                                    placeholder: "0%"
                                }} />
                                <InputField className="g-col-2" label={{ name: "Lot Size" }}
                                    required={true}
                                    input={{
                                        type: "number",
                                        name: "lot_size",
                                        value: values.lot_size,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "80 Qty",

                                    }} validation={{
                                        isInvalid: touched.lot_size && errors.lot_size,
                                        message: errors.lot_size,
                                    }} />
                                <InputField className="g-col-2" label={{ name: "Available Qty" }}
                                    required={true}
                                    input={{
                                        type: "number",
                                        name: "available_qty",
                                        value: values.available_qty,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "650/100 pk",

                                    }} validation={{
                                        isInvalid: touched.available_qty && errors.available_qty,
                                        message: errors.available_qty,
                                    }} />

                                <InputField className="g-col-2" label={{ name: "Offer Start Date" }}
                                    required={true}
                                    input={{
                                        type: "date",
                                        name: "start_date",
                                        value: values.start_date,
                                        onChange: handleChange,
                                        onBlur: handleBlur,

                                    }} validation={{
                                        isInvalid: touched.start_date && errors.start_date,
                                        message: errors.start_date,
                                    }} />
                                <InputField className="g-col-2" label={{ name: "Offer End Date" }}
                                    required={true}
                                    input={{
                                        type: "date",
                                        name: "end_date",
                                        value: values.end_date,
                                        onChange: handleChange,
                                        onBlur: handleBlur,

                                    }} validation={{
                                        isInvalid: touched.end_date && errors.end_date,
                                        message: errors.end_date,
                                    }} />

                                <Form.Group controlId="booking_deposit" className="g-col-2">
                                    <FormLabel labelName="Booking Deposit" required={true} id="booking_deposit" />
                                    <InputGroup>
                                        <InputGroup.Text id="booking_deposit-addon">₹</InputGroup.Text>
                                        <Form.Control
                                            id="booking_deposit"
                                            type="number"
                                            name="booking_deposit"
                                            value={values.booking_deposit}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="5000"
                                            aria-label="Booking Deposit"
                                            aria-describedby="booking_deposit-addon"
                                        />
                                    </InputGroup>
                                    {errors.booking_deposit && touched.booking_deposit && <FormFeedback message={errors.booking_deposit} />}
                                </Form.Group>

                                <CustomSelect
                                    className="g-col-3"
                                    label={{ name: "Base State" }}
                                    name="state_id"
                                    required={true}
                                    value={values.state_id}
                                    onChange={(name, val) => {
                                        setFieldValue("state_id", val);
                                        setFieldValue("city_id", "");
                                    }}
                                    isClearable
                                    fetchOptions={fetchStatesOptions}
                                    placeholder="Select State"
                                    validation={{
                                        isInvalid: touched.state_id && errors.state_id,
                                        message: errors.state_id,
                                    }}
                                />


                                <CustomSelect
                                    className="g-col-3"
                                    label={{ name: "Base City" }}
                                    name="city_id"
                                    required={true}
                                    isClearable
                                    value={values.city_id}
                                    onChange={setFieldValue}
                                    fetchOptions={fetchCitiesOptionsForState(values.state_id)}
                                    placeholder="Select City"
                                    validation={{
                                        isInvalid: touched.city_id && errors.city_id,
                                        message: errors.city_id,
                                    }}
                                    isDisabled={!values.state_id}
                                />

                                <InputField
                                    className="g-col-3"
                                    label={{ name: "Delivery To be taken before" }}
                                    input={{
                                        name: "delivery_taken_before",
                                        value: values.delivery_taken_before,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "e.g. 15 Days",
                                    }}
                                    validation={{
                                        isInvalid: touched.delivery_taken_before && errors.delivery_taken_before,
                                        message: errors.delivery_taken_before,
                                    }}
                                />
                                <InputField
                                    className="g-col-3"
                                    label={{ name: "Delivery Charges" }}
                                    input={{
                                        name: "delivery_charges",
                                        value: values.delivery_charges,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "e.g. ₹50 per unit",
                                    }}
                                    validation={{
                                        isInvalid: touched.delivery_charges && errors.delivery_charges,
                                        message: errors.delivery_charges,
                                    }}
                                />
                                <InputField
                                    className="g-col-3"
                                    label={{ name: "Delivery Start Date" }}
                                    input={{
                                        type: "date",
                                        name: "delivery_start_date",
                                        value: values.delivery_start_date,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                    }}
                                    validation={{
                                        isInvalid: touched.delivery_start_date && errors.delivery_start_date,
                                        message: errors.delivery_start_date,
                                    }}
                                />
                                <CustomSelect
                                    label={{ name: "Billing From" }}
                                    name="billing_from"
                                    required={true}
                                    value={values.billing_from}
                                    onChange={(opt) => setFieldValue("billing_from", opt ? opt.value : "")}
                                    options={config.billing_options || []}
                                    placeholder="Select Billing"
                                    validation={{
                                        isInvalid: touched.billing_from && errors.billing_from,
                                        message: errors.billing_from,
                                    }}
                                    className="g-col-3"
                                />
                                <InputField
                                    className="g-col-12"
                                    label={{ name: "Payment Credit Policy" }}
                                    input={{
                                        name: "payment_credit_policy",
                                        as: "textarea",
                                        rows: 4,
                                        value: values.payment_credit_policy,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "Standard payment terms and credit policy details...",
                                    }}
                                />
                            </FormSection>

                            {/* ── SERVICEABILITY ── */}
                            <FormSection title="Serviceability">

                                <CustomSelect
                                    label={{ name: "Serviceability Level" }}
                                    name="serviceability_type"
                                    required={true}
                                    value={values.serviceability_type}
                                    options={serviceabilityOptions}
                                    onChange={(opt) => {
                                        setFieldValue("serviceability_type", opt ? opt.value : "");
                                        setFieldValue("serviceable_ids", []);
                                    }}
                                    placeholder="Select Level"
                                    validation={{
                                        isInvalid: touched.serviceability_type && errors.serviceability_type,
                                        message: errors.serviceability_type,
                                    }}
                                    menuPosition="fixed"
                                    className="g-col-4"
                                />

                                {['states', 'cities'].includes(values.serviceability_type) && (
                                    <div className="g-col-8">
                                        <CustomSelect
                                            isMulti
                                            closeMenuOnSelect={false}
                                            hideSelectedOptions={false}
                                            required={true}
                                            label={{ name: values.serviceability_type === 'states' ? "Select States" : "Select Cities" }}
                                            name="serviceable_ids"
                                            selectProps={{
                                                components: { Option: CheckboxOption, },
                                                styles: customOptionStyle
                                            }}
                                            value={values.serviceable_ids}
                                            onChange={setFieldValue}
                                            fetchOptions={values.serviceability_type === 'states' ? fetchStatesOptions : fetchCitiesOptionsForState()}
                                            placeholder={values.serviceability_type === 'states' ? "Search and select states..." : "Search and select cities..."}
                                            validation={{
                                                isInvalid: touched.serviceable_ids && errors.serviceable_ids,
                                                message: errors.serviceable_ids,
                                            }}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                )}
                            </FormSection>


                            {/* ── SELLER INFORMATION ── */}
                            <FormSection title="Seller Information">

                                <CustomSelect
                                    label={{ name: "Company Type" }}
                                    name="company_type"
                                    required={true}
                                    value={values.company_type}
                                    onChange={(opt) => setFieldValue("company_type", opt ? opt.value : "")}
                                    options={config.company_types || []}
                                    placeholder="Select Type"
                                    validation={{
                                        isInvalid: touched.company_type && errors.company_type,
                                        message: errors.company_type,
                                    }}
                                    className="g-col-4"
                                />
                                <InputField
                                    className="g-col-4"
                                    label={{ name: "Marketed By" }}
                                    required={true}
                                    input={{
                                        name: "marketed_by",
                                        value: values.marketed_by,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "Metro Content",
                                    }}
                                    validation={{
                                        isInvalid: touched.marketed_by && errors.marketed_by,
                                        message: errors.marketed_by,
                                    }}
                                />
                                <InputField
                                    className="g-col-4"
                                    label={{ name: "Manufactured By" }}
                                    required={true}
                                    input={{
                                        name: "manufactured_by",
                                        value: values.manufactured_by,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "Metro Content",
                                    }}
                                    validation={{
                                        isInvalid: touched.manufactured_by && errors.manufactured_by,
                                        message: errors.manufactured_by,
                                    }}
                                />
                                <InputField
                                    className="g-col-12"
                                    label={{ name: "Company/Plant Address" }}
                                    required={true}
                                    input={{
                                        name: "plant_address",
                                        as: "textarea",
                                        rows: 3,
                                        value: values.plant_address,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        placeholder: "G-Wing, Ground Floor, Reliable Tech Park, Andheri (E), Mumbai: 400-963",
                                    }}
                                    validation={{
                                        isInvalid: touched.plant_address && errors.plant_address,
                                        message: errors.plant_address,
                                    }}
                                />

                            </FormSection>

                            {/* ── MEDIA INFORMATION ── */}
                            <FormSection title="Media Information">
                                <div className="g-col-12">
                                    { /* Hidden file inputs */}
                                    <input
                                        id="bulk-image-input"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={async (e) => {
                                            const files = Array.from(e.target.files || []);
                                            if (files.length > 0) {
                                                const company_id = values.company_id || details?.seller_id;
                                                if (!company_id) {
                                                    // This should be handled by onClick/title, but as a safeguard:
                                                    toast.error("Please select a company first");
                                                    return;
                                                }
                                                setLoading(true);
                                                try {
                                                    const uploadResult = await dispatch(uploadMarketplaceMedia({
                                                        company_id,
                                                        files
                                                    })).unwrap();
                                                    const newMedia = uploadResult.data || [];
                                                    setFieldValue("existingImages", [...(values.existingImages || []), ...newMedia]);
                                                    toast.success(`${newMedia.length} images uploaded successfully`);
                                                } catch (err) {
                                                    console.error("Upload error :::", err);
                                                    toast.error(typeof err === 'string' ? err : "Failed to upload images");
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }
                                            e.target.value = "";
                                        }}
                                    />
                                    <input
                                        id="bulk-video-input"
                                        type="file"
                                        accept="video/*"
                                        style={{ display: "none" }}
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const company_id = values.company_id || details?.seller_id;
                                                if (!company_id) {
                                                    toast.error("Please select a company first");
                                                    return;
                                                }
                                                setLoading(true);
                                                try {
                                                    const uploadResult = await dispatch(uploadMarketplaceMedia({
                                                        company_id,
                                                        files: [file]
                                                    })).unwrap();
                                                    const newMedia = uploadResult.data?.[0];
                                                    if (newMedia) {
                                                        setFieldValue("existingVideo", newMedia);
                                                        toast.success("Video uploaded successfully");
                                                    }
                                                } catch (err) {
                                                    toast.error(typeof err === 'string' ? err : "Failed to upload video");
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }
                                            e.target.value = "";
                                        }}
                                    />
                                    <input
                                        id="bulk-brochure-input"
                                        type="file"
                                        accept=".pdf"
                                        style={{ display: "none" }}
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const company_id = values.company_id || details?.seller_id;
                                                if (!company_id) {
                                                    toast.error("Please select a company first");
                                                    return;
                                                }
                                                setLoading(true);
                                                try {
                                                    const uploadResult = await dispatch(uploadMarketplaceMedia({
                                                        company_id,
                                                        files: [file]
                                                    })).unwrap();
                                                    const newMedia = uploadResult.data?.[0];
                                                    if (newMedia) {
                                                        setFieldValue("existingBrochure", newMedia);
                                                        toast.success("Brochure uploaded successfully");
                                                    }
                                                } catch (err) {
                                                    toast.error(typeof err === 'string' ? err : "Failed to upload brochure");
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }
                                            e.target.value = "";
                                        }}
                                    />
                                    <input
                                        id="bulk-aplus-image-input"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={async (e) => {
                                            const files = Array.from(e.target.files || []);
                                            if (files.length > 0) {
                                                const company_id = values.company_id || details?.seller_id;
                                                if (!company_id) {
                                                    toast.error("Please select a company first");
                                                    return;
                                                }
                                                setLoading(true);
                                                try {
                                                    const uploadResult = await dispatch(uploadMarketplaceMedia({
                                                        company_id,
                                                        files,
                                                        collection: "a_plus"
                                                    })).unwrap();
                                                    const newMedia = uploadResult.data || [];
                                                    setFieldValue("existingAPlusImages", [...(values.existingAPlusImages || []), ...newMedia]);
                                                    toast.success(`${newMedia.length} A+ content images uploaded successfully`);
                                                } catch (err) {
                                                    console.error("Upload error :::", err);
                                                    toast.error(typeof err === 'string' ? err : "Failed to upload A+ content images");
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }
                                            e.target.value = "";
                                        }}
                                    />

                                    {/* ── A+ Content Photos ── */}
                                    <p className="fw-medium mb-2" style={{ fontSize: "13px", color: "#444" }}>A+ Content Photos <span className="text-danger">*</span></p>
                                    <div
                                        className="p-3 mb-4"
                                        style={{
                                            border: "1.5px dashed #ccc",
                                            borderRadius: "8px",
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "12px",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        {/* Existing A+ images */}
                                        {values.existingAPlusImages.map((img, idx) => (
                                            <div key={`aplus-ex-${idx}`} style={{
                                                width: 145, background: "#fff",
                                                border: "1px solid #e5e5e5", borderRadius: "8px",
                                                overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
                                            }}>
                                                <div className="position-relative" style={{ height: 110 }}>
                                                    <img
                                                        src={img.url}
                                                        alt=""
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                    <span style={{
                                                        position: "absolute", top: "50%", left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        background: "rgba(0,0,0,0.45)", borderRadius: "50%",
                                                        width: 36, height: 36, display: "flex",
                                                        alignItems: "center", justifyContent: "center"
                                                    }}>
                                                        <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                                                            <path d="M2.5 7l3.5 3.5 5.5-5.5" stroke="#28c76f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div style={{ padding: "6px 8px 0" }}>
                                                    <div style={{ fontSize: "11px", color: "#333", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {img.url?.split("/").pop() || "A+ Image.png"}
                                                    </div>
                                                    <div style={{ fontSize: "10px", color: "#888", marginBottom: 6 }}>—</div>
                                                </div>
                                                <div style={{ padding: "0 8px 8px" }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveMedia(img.id, 'a_plus_photos', setFieldValue, values)}
                                                        style={{
                                                            width: "100%", fontSize: "11px", color: "#333",
                                                            background: "#fff", border: "1px solid #ddd",
                                                            borderRadius: "5px", cursor: "pointer",
                                                            padding: "5px 0", fontWeight: 500
                                                        }}
                                                    >
                                                        Remove File
                                                    </button>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Add A+ File card */}
                                        <OverlayTrigger
                                            placement="top"
                                            show={!(values.company_id || details?.seller_id) ? undefined : false}
                                            overlay={
                                                !(values.company_id || details?.seller_id) ? (
                                                    <Tooltip id="tooltip-aplus-images">
                                                        Please select a company first
                                                    </Tooltip>
                                                ) : (
                                                    <></>
                                                )
                                            }
                                        >
                                            <div
                                                style={{
                                                    width: 145, background: "#fff",
                                                    border: "1px solid #e5e5e5", borderRadius: "8px",
                                                    overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                                    cursor: (values.company_id || details?.seller_id) ? "pointer" : "not-allowed",
                                                    opacity: (values.company_id || details?.seller_id) ? 1 : 0.6
                                                }}
                                                onClick={() => {
                                                    if (values.company_id || details?.seller_id) {
                                                        document.getElementById("bulk-aplus-image-input").click();
                                                    }
                                                }}
                                            >
                                                <div style={{
                                                    height: 110, background: "#f8f9fa",
                                                    display: "flex", flexDirection: "column",
                                                    alignItems: "center", justifyContent: "center", gap: 6, padding: "0 8px"
                                                }}>
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.5">
                                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <span style={{ fontSize: "10px", color: "#888", lineHeight: 1.3, textAlign: "center" }}>
                                                        Drop files here or click to upload
                                                    </span>
                                                </div>
                                                <div style={{ padding: "6px 8px 8px" }}>
                                                    <button
                                                        type="button"
                                                        style={{
                                                            width: "100%", fontSize: "11px", color: "#333",
                                                            background: "#fff", border: "1px solid #ddd",
                                                            borderRadius: "5px", cursor: (values.company_id || details?.seller_id) ? "pointer" : "not-allowed",
                                                            padding: "5px 0", fontWeight: 500
                                                        }}
                                                    >
                                                        Add A+ File
                                                    </button>
                                                </div>
                                            </div>
                                        </OverlayTrigger>
                                    </div>
                                    {errors.existingAPlusImages && touched.existingAPlusImages && (
                                        <FormFeedback message={errors.existingAPlusImages} />
                                    )}

                                    {/* ── Bulk Offer Image ── */}
                                    <p className="fw-medium mb-2" style={{ fontSize: "13px", color: "#444" }}>Bulk Offer Image <span className="text-danger">*</span></p>
                                    <div
                                        className="p-3 mb-4"
                                        style={{
                                            border: "1.5px dashed #ccc",
                                            borderRadius: "8px",
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "12px",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        {/* Existing images */}
                                        {values.existingImages.map((img, idx) => (
                                            <div key={`ex-${idx}`} style={{
                                                width: 145, background: "#fff",
                                                border: "1px solid #e5e5e5", borderRadius: "8px",
                                                overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
                                            }}>
                                                <div className="position-relative" style={{ height: 110 }}>
                                                    <img
                                                        src={img.url}
                                                        alt=""
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                    {/* Centered dark semi-transparent circle with green tick */}
                                                    <span style={{
                                                        position: "absolute", top: "50%", left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        background: "rgba(0,0,0,0.45)", borderRadius: "50%",
                                                        width: 36, height: 36, display: "flex",
                                                        alignItems: "center", justifyContent: "center"
                                                    }}>
                                                        <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                                                            <path d="M2.5 7l3.5 3.5 5.5-5.5" stroke="#28c76f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div style={{ padding: "6px 8px 0" }}>
                                                    <div style={{ fontSize: "11px", color: "#333", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {img.url?.split("/").pop() || "Offer Image.png"}
                                                    </div>
                                                    <div style={{ fontSize: "10px", color: "#888", marginBottom: 6 }}>—</div>
                                                </div>
                                                <div style={{ padding: "0 8px 8px" }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveMedia(img.id, 'photos', setFieldValue, values)}
                                                        style={{
                                                            width: "100%", fontSize: "11px", color: "#333",
                                                            background: "#fff", border: "1px solid #ddd",
                                                            borderRadius: "5px", cursor: "pointer",
                                                            padding: "5px 0", fontWeight: 500
                                                        }}
                                                    >
                                                        Remove File
                                                    </button>
                                                </div>
                                            </div>
                                        ))}

                                        {/* New images */}
                                        {values.images.map((img, idx) => (
                                            <div key={`new-${idx}`} style={{
                                                width: 145, background: "#fff",
                                                border: "1px solid #e5e5e5", borderRadius: "8px",
                                                overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
                                            }}>
                                                <div className="position-relative" style={{ height: 110 }}>
                                                    <img
                                                        src={URL.createObjectURL(img)}
                                                        alt=""
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                    {/* Centered dark semi-transparent circle with green tick */}
                                                    <span style={{
                                                        position: "absolute", top: "50%", left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        background: "rgba(0,0,0,0.45)", borderRadius: "50%",
                                                        width: 36, height: 36, display: "flex",
                                                        alignItems: "center", justifyContent: "center"
                                                    }}>
                                                        <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                                                            <path d="M2.5 7l3.5 3.5 5.5-5.5" stroke="#28c76f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div style={{ padding: "6px 8px 0" }}>
                                                    <div style={{ fontSize: "11px", color: "#333", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {img.name}
                                                    </div>
                                                    <div style={{ fontSize: "10px", color: "#888", marginBottom: 6 }}>{(img.size / (1024 * 1024)).toFixed(1)} MB</div>
                                                </div>
                                                <div style={{ padding: "0 8px 8px" }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const updated = [...values.images];
                                                            updated.splice(idx, 1);
                                                            setFieldValue("images", updated);
                                                        }}
                                                        style={{
                                                            width: "100%", fontSize: "11px", color: "#333",
                                                            background: "#fff", border: "1px solid #ddd",
                                                            borderRadius: "5px", cursor: "pointer",
                                                            padding: "5px 0", fontWeight: 500
                                                        }}
                                                    >
                                                        Remove File
                                                    </button>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Add File card */}
                                        <OverlayTrigger
                                            placement="top"
                                            show={!(values.company_id || details?.seller_id) ? undefined : false}
                                            overlay={
                                                !(values.company_id || details?.seller_id) ? (
                                                    <Tooltip id="tooltip-images">
                                                        Please select a company first
                                                    </Tooltip>
                                                ) : (
                                                    <></>
                                                )
                                            }
                                        >
                                            <div
                                                style={{
                                                    width: 145, background: "#fff",
                                                    border: "1px solid #e5e5e5", borderRadius: "8px",
                                                    overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                                    cursor: (values.company_id || details?.seller_id) ? "pointer" : "not-allowed",
                                                    opacity: (values.company_id || details?.seller_id) ? 1 : 0.6
                                                }}
                                                onClick={() => {
                                                    if (values.company_id || details?.seller_id) {
                                                        document.getElementById("bulk-image-input").click();
                                                    }
                                                }}
                                            >
                                                <div style={{
                                                    height: 110, background: "#f8f9fa",
                                                    display: "flex", flexDirection: "column",
                                                    alignItems: "center", justifyContent: "center", gap: 6, padding: "0 8px"
                                                }}>
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.5">
                                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <span style={{ fontSize: "10px", color: "#888", lineHeight: 1.3, textAlign: "center" }}>
                                                        Drop files here or click to upload
                                                    </span>
                                                </div>
                                                <div style={{ padding: "6px 8px 8px" }}>
                                                    <button
                                                        type="button"
                                                        style={{
                                                            width: "100%", fontSize: "11px", color: "#333",
                                                            background: "#fff", border: "1px solid #ddd",
                                                            borderRadius: "5px", cursor: (values.company_id || details?.seller_id) ? "pointer" : "not-allowed",
                                                            padding: "5px 0", fontWeight: 500
                                                        }}
                                                    >
                                                        Add File
                                                    </button>
                                                </div>
                                            </div>
                                        </OverlayTrigger>
                                    </div>
                                    {errors.existingImages && touched.existingImages && (
                                        <FormFeedback message={errors.existingImages} />
                                    )}
                                </div>
                                {/* ── Video & Brochure side by side ── */}

                                {/* Bulk Offer Videos */}
                                <div className="g-col-3">
                                    <p className="fw-medium mb-2" style={{ fontSize: "13px", color: "#444" }}>Bulk Offer Videos</p>
                                    <div
                                        style={{
                                            border: "1.5px dashed #ccc",
                                            borderRadius: "8px",
                                            padding: "16px",
                                            minHeight: "160px",
                                        }}
                                    >
                                        {(values.existingVideo || values.video) ? (
                                            <div style={{ textAlign: "center" }}>
                                                <div className="position-relative mb-2" style={{ height: 170, borderRadius: "6px", overflow: "hidden" }}>
                                                    {values.video ? (
                                                        /* New video file — live preview */
                                                        <video
                                                            src={URL.createObjectURL(values.video)}
                                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                            muted
                                                            preload="metadata"
                                                        />
                                                    ) : values.existingVideo?.thumbnail_url ? (
                                                        <img
                                                            src={values.existingVideo.thumbnail_url}
                                                            alt="video"
                                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: "100%", height: "100%", background: "#f0f0f0",
                                                            display: "flex", alignItems: "center", justifyContent: "center"
                                                        }}>
                                                            <svg width="40" height="40" viewBox="0 0 24 24" fill="#bbb">
                                                                <path d="M8 6.82v10.36c0 .79.87 1.27 1.54.84l8.14-5.18a1 1 0 000-1.68L9.54 5.98A1 1 0 008 6.82z" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                    {/* Centered dark semi-transparent circle with green tick */}
                                                    <span style={{
                                                        position: "absolute", top: "50%", left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        background: "rgba(0,0,0,0.45)", borderRadius: "50%",
                                                        width: 42, height: 42, display: "flex",
                                                        alignItems: "center", justifyContent: "center"
                                                    }}>
                                                        <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                                                            <path d="M2.5 7l3.5 3.5 5.5-5.5" stroke="#28c76f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#333", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {values.video?.name || values.existingVideo?.url?.split("/").pop() || "Offer_Video.mp4"}
                                                </div>
                                                <div style={{ fontSize: "11px", color: "#888", marginBottom: 8 }}>
                                                    {values.video ? `${(values.video.size / (1024 * 1024)).toFixed(1)} MB` : "—"}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveMedia(values.existingVideo?.id, 'video', setFieldValue)}
                                                    style={{
                                                        width: "100%", fontSize: "12px", color: "#333",
                                                        background: "#fff", border: "1px solid #ddd",
                                                        borderRadius: "6px", cursor: "pointer",
                                                        padding: "6px 0", fontWeight: 500
                                                    }}
                                                >
                                                    Remove File
                                                </button>
                                            </div>
                                        ) : (
                                            <OverlayTrigger
                                                placement="top"
                                                show={!(values.company_id || details?.seller_id) ? undefined : false}
                                                overlay={
                                                    !(values.company_id || details?.seller_id) ? (
                                                        <Tooltip id="tooltip-video">
                                                            Please select a company first
                                                        </Tooltip>
                                                    ) : (
                                                        <></>
                                                    )
                                                }
                                            >
                                                <div
                                                    style={{
                                                        cursor: (values.company_id || details?.seller_id) ? "pointer" : "not-allowed",
                                                        height: "100%", minHeight: 120, display: "flex",
                                                        flexDirection: "column", alignItems: "center",
                                                        justifyContent: "center", gap: 6,
                                                        opacity: (values.company_id || details?.seller_id) ? 1 : 0.6
                                                    }}
                                                    onClick={() => {
                                                        if (values.company_id || details?.seller_id) {
                                                            document.getElementById("bulk-video-input").click();
                                                        }
                                                    }}
                                                >
                                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
                                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <span style={{ fontSize: "11px", color: "#aaa" }}>Drop video or click to upload</span>
                                                </div>
                                            </OverlayTrigger>
                                        )}
                                    </div>
                                </div>

                                {/* Bulk Offer Brochure */}
                                <div className="g-col-3">
                                    <p className="fw-medium mb-2" style={{ fontSize: "13px", color: "#444" }}>Bulk Offer Brochure</p>
                                    <div
                                        style={{
                                            border: "1.5px dashed #ccc",
                                            borderRadius: "8px",
                                            padding: "16px",
                                            minHeight: "160px",
                                        }}
                                    >
                                        {(values.existingBrochure || values.brochure) ? (
                                            <div style={{ textAlign: "center" }}>
                                                <div className="position-relative mb-2" style={{ height: 170, borderRadius: "6px", overflow: "hidden" }}>
                                                    {values.brochure ? (
                                                        /* New PDF file — embedded preview */
                                                        <embed
                                                            src={URL.createObjectURL(values.brochure)}
                                                            type="application/pdf"
                                                            style={{ width: "100%", height: "100%", border: "none", pointerEvents: "none" }}
                                                        />
                                                    ) : (
                                                        /* Existing brochure — show PDF icon */
                                                        <div style={{
                                                            width: "100%", height: "100%", background: "#e0e0e0",
                                                            display: "flex", alignItems: "center", justifyContent: "center"
                                                        }}>
                                                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.4">
                                                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeLinecap="round" strokeLinejoin="round" />
                                                                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                    {/* Centered dark semi-transparent circle with green tick */}
                                                    <span style={{
                                                        position: "absolute", top: "50%", left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        background: "rgba(0,0,0,0.45)", borderRadius: "50%",
                                                        width: 42, height: 42, display: "flex",
                                                        alignItems: "center", justifyContent: "center"
                                                    }}>
                                                        <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                                                            <path d="M2.5 7l3.5 3.5 5.5-5.5" stroke="#28c76f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#333", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {values.brochure?.name || values.existingBrochure?.url?.split("/").pop() || "Offer_brochure.pdf"}
                                                </div>
                                                <div style={{ fontSize: "11px", color: "#888", marginBottom: 8 }}>
                                                    {values.brochure ? `${(values.brochure.size / (1024 * 1024)).toFixed(1)} MB` : "—"}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveMedia(values.existingBrochure?.id, 'document', setFieldValue)}
                                                    style={{
                                                        width: "100%", fontSize: "12px", color: "#333",
                                                        background: "#fff", border: "1px solid #ddd",
                                                        borderRadius: "6px", cursor: "pointer",
                                                        padding: "6px 0", fontWeight: 500
                                                    }}
                                                >
                                                    Remove File
                                                </button>
                                            </div>
                                        ) : (
                                            <OverlayTrigger
                                                placement="top"
                                                show={!(values.company_id || details?.seller_id) ? undefined : false}
                                                overlay={
                                                    !(values.company_id || details?.seller_id) ? (
                                                        <Tooltip id="tooltip-brochure">
                                                            Please select a company first
                                                        </Tooltip>
                                                    ) : (
                                                        <></>
                                                    )
                                                }
                                            >
                                                <div
                                                    style={{
                                                        cursor: (values.company_id || details?.seller_id) ? "pointer" : "not-allowed",
                                                        height: "100%", minHeight: 120, display: "flex",
                                                        flexDirection: "column", alignItems: "center",
                                                        justifyContent: "center", gap: 6,
                                                        opacity: (values.company_id || details?.seller_id) ? 1 : 0.6
                                                    }}
                                                    onClick={() => {
                                                        if (values.company_id || details?.seller_id) {
                                                            document.getElementById("bulk-brochure-input").click();
                                                        }
                                                    }}
                                                >
                                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
                                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <span style={{ fontSize: "11px", color: "#aaa" }}>Drop brochure or click to upload</span>
                                                </div>
                                            </OverlayTrigger>
                                        )}
                                    </div>
                                </div>

                            </FormSection>
                        </Form>
                    );
                }}
            </Formik>
        </Container >
    );
};

export default BulkOfferForm;
